/**
 * De vraag die het banen-canvas stelt wanneer een stroom de stroomregel zou
 * breken (regel A, 2026-09-23): een tweede stroom die in een stap uitkomt, of
 * een tweede die een gewone stap verlaat.
 *
 * Het canvas weigert niet, maar vraagt wat het samenkomen of splitsen
 * BETEKENT en zet dan zelf de vertakking ertussen. Zo kiest de modelleur de
 * betekenis — dat was het punt — en hoeft 'm niet zelf te tekenen.
 */
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

export type VertakkingSoort = "xor" | "and" | "of";

export interface VertakkingVraag {
  x: number;
  y: number;
  /** Samenkomen vóór een stap, of splitsen erna. */
  richting: "samen" | "splits";
  /** De stroom die getrokken werd. */
  van: string;
  naar: string;
  /** De stroom die er al was en die door de vertakking gaat lopen. */
  bestaand: string;
}

export function VertakkingKeuze({
  vraag,
  onKies,
  onClose,
}: {
  vraag: VertakkingVraag;
  onKies: (soort: VertakkingSoort) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const opKlik = (e: MouseEvent) => {
      if (e.target instanceof Node && ref.current?.contains(e.target)) return;
      onClose();
    };
    const opToets = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("mousedown", opKlik);
    window.addEventListener("keydown", opToets);
    return () => {
      window.removeEventListener("mousedown", opKlik);
      window.removeEventListener("keydown", opToets);
    };
  }, [onClose]);

  const knop = "block w-full px-3 py-1.5 text-left text-slate-700 hover:bg-slate-100";
  const soorten: VertakkingSoort[] = ["xor", "and", "of"];

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: menu-oppervlak; de knoppen erin dragen de interactie
    <div
      ref={ref}
      className="absolute z-20 w-[280px] rounded-md border border-slate-400 bg-white py-1 text-sm shadow-slate-900/20 shadow-xl ring-1 ring-slate-900/5"
      style={{ left: vraag.x, top: vraag.y }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <p className="border-slate-200 border-b px-3 pt-1 pb-2 text-slate-600 text-xs">
        {t(
          vraag.richting === "samen"
            ? "swimlane.flowRule.mergeQuestion"
            : "swimlane.flowRule.splitQuestion",
        )}
      </p>
      {soorten.map((soort) => (
        <button key={soort} type="button" className={knop} onClick={() => onKies(soort)}>
          <span className="font-medium">{t(`swimlane.flowRule.${soort}`)}</span>
          <span className="block text-slate-500 text-xs">
            {t(`swimlane.flowRule.${vraag.richting}.${soort}`)}
          </span>
        </button>
      ))}
      <button
        type="button"
        className="block w-full border-slate-200 border-t px-3 py-1.5 text-left text-slate-500 hover:bg-slate-100"
        onClick={onClose}
      >
        {t("swimlane.flowRule.cancel")}
      </button>
    </div>
  );
}
